"use client";

import Image from "next/image";
import Link from "next/link";

const CALENDLY_URL =
  "https://calendly.com/kathleen-sheldon-silveroakdigitalmarketing/15-minute-audit";

const checks = [
  {
    headline: "Where you rank for 10 searches",
    sub: "the searches your customers are actually using",
  },
  {
    headline: "Which competitors are outranking you",
    sub: "and exactly why Google favors them",
  },
  {
    headline: "What you can do to climb higher",
    sub: "a clear, actionable path to more visibility",
  },
];

const trustItems = [
  { label: "Veteran-Owned Business", icon: "🎖️" },
  { label: "Trusted by Local Businesses", icon: "🤝" },
  { label: "15-Min Review · Zero Obligation", icon: "⏱️" },
];

export default function FreeAuditContent() {
  return (
    <main className="min-h-screen bg-white font-sans">
      {/* ── Top bar ── */}
      <header className="flex items-center justify-center py-5 border-b border-gray-100 bg-white">
        <Image
          src="/logo.png"
          alt="Silver Oak Digital Marketing"
          width={200}
          height={60}
          className="h-12 w-auto object-contain"
          priority
        />
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-white">
        {/* subtle grid backdrop */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#1a1a1a 1px,transparent 1px),linear-gradient(90deg,#1a1a1a 1px,transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative mx-auto max-w-3xl px-6 pt-14 pb-10 text-center">
          {/* badge */}
          <span className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-1.5 text-sm font-semibold text-green-700 mb-6">
            <span>📍</span> Free Google Ranking Audit
          </span>

          {/* headline */}
          <h1
            className="font-display text-5xl md:text-6xl font-black leading-[1.05] tracking-tight text-gray-900 mb-5"
            style={{ fontFamily: "var(--font-outfit)" }}
          >
            Can Your Customers
            <br />
            <span className="text-[#34A853]">Actually Find You?</span>
          </h1>

          <p className="text-xl text-gray-600 max-w-xl mx-auto mb-8">
            Most local businesses are invisible on Google and don&apos;t even
            know it. We&apos;ll show you exactly where you stand — and how to fix
            it — in a free 15-minute review.
          </p>

          <Link
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#34A853] px-8 py-4 text-lg font-bold text-white shadow-lg hover:bg-[#2d9248] transition-colors"
          >
            📅 Book My Free Audit
          </Link>

          <p className="mt-3 text-sm text-gray-400">
            15-minute call · No credit card · Zero obligation
          </p>
        </div>
      </section>

      {/* ── What you'll learn ── */}
      <section className="bg-gray-50 py-14">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
            In your free audit, we&apos;ll show you
          </p>
          <h2
            className="font-display text-3xl font-bold text-center text-gray-900 mb-10"
            style={{ fontFamily: "var(--font-outfit)" }}
          >
            Exactly What&apos;s Holding You Back
          </h2>

          <div className="grid gap-5 md:grid-cols-3">
            {checks.map((item) => (
              <div
                key={item.headline}
                className="flex flex-col gap-2 rounded-2xl border border-green-100 bg-white p-6 shadow-sm"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#34A853] text-white text-lg font-bold">
                  ✓
                </div>
                <p className="font-semibold text-gray-900">{item.headline}</p>
                <p className="text-sm text-gray-500">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social proof strip ── */}
      <section className="border-y border-gray-100 bg-white py-8">
        <div className="mx-auto max-w-3xl px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            {trustItems.map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-gray-700">
                <span className="text-xl">{t.icon}</span>
                <span className="font-medium text-sm">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
            Simple process
          </p>
          <h2
            className="font-display text-3xl font-bold text-center text-gray-900 mb-10"
            style={{ fontFamily: "var(--font-outfit)" }}
          >
            How It Works
          </h2>

          <ol className="space-y-6">
            {[
              {
                step: "1",
                title: "Book your free 15-minute slot",
                desc: "Pick a time that works for you using the link below. No forms to fill out in advance.",
              },
              {
                step: "2",
                title: "We run your Google visibility audit",
                desc: "We check the 10 searches your customers are actually using and map exactly where you appear vs. your competitors.",
              },
              {
                step: "3",
                title: "You get a clear action plan",
                desc: "We walk you through the findings live and tell you precisely what it would take to climb higher — no jargon, no pressure.",
              },
            ].map((item) => (
              <li key={item.step} className="flex gap-5 items-start">
                <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-[#1a3d1e] text-white font-bold text-lg">
                  {item.step}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-0.5">{item.title}</p>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="bg-[#1a3d1e] py-16 text-center">
        <div className="mx-auto max-w-2xl px-6">
          <h2
            className="font-display text-4xl font-black text-white mb-4"
            style={{ fontFamily: "var(--font-outfit)" }}
          >
            Ready to See Where You Stand?
          </h2>
          <p className="text-green-200 text-lg mb-8">
            It&apos;s free, it&apos;s fast, and it could change how visible your
            business is to every customer searching for what you do.
          </p>
          <Link
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-bold text-[#1a3d1e] shadow-lg hover:bg-green-50 transition-colors"
          >
            📅 Book My Free Audit Now
          </Link>
          <p className="mt-4 text-green-300 text-sm">
            (919) 307-5165 &nbsp;·&nbsp;{" "}
            <a
              href="https://www.silveroakdigitalmarketing.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white transition-colors"
            >
              SilverOakDigitalMarketing.com
            </a>
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-white py-6 text-center text-xs text-gray-400 border-t border-gray-100">
        © {new Date().getFullYear()} Silver Oak Digital Marketing · Veteran-Owned ·{" "}
        <Link href="/privacy-policy" className="underline hover:text-gray-600">
          Privacy Policy
        </Link>
      </footer>
    </main>
  );
}
